import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, ArrowRight, Share2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ShareDialog } from "@/components/shared/ShareDialog";
import { api, API, getErrMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function IntelligenceDetail() {
  const { id } = useParams();
  const [memo, setMemo] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/intelligence/${id}`).then((r) => setMemo(r.data));
  }, [id]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("frontera_token");
      const res = await fetch(`${API}/intelligence/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${memo.title.replace(/[^A-Za-z0-9]+/g, "-").slice(0, 80)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("PDF export failed", { description: err.message || getErrMsg(err) });
    } finally {
      setDownloading(false);
    }
  };

  if (!memo) return <DashboardLayout><div className="p-10 text-muted-foreground">Loading…</div></DashboardLayout>;

  const shareText = `${memo.title} — ${memo.subtitle || "Institutional deep-dive from Frontera Intelligence."}`;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/intelligence")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-to-intelligence">
            <ArrowLeft className="h-3 w-3" /> All memos
          </button>
          <div className="flex items-center gap-4">
            {!memo.locked && (user?.tier === "professional" || user?.tier === "institutional") && (
              <button
                onClick={downloadPDF}
                disabled={downloading}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 disabled:opacity-50"
                data-testid="memo-download-pdf"
              >
                <FileDown className="h-3.5 w-3.5" /> {downloading ? "Preparing…" : "Download PDF"}
              </button>
            )}
            <button
              onClick={() => setShareOpen(true)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
              data-testid="share-memo-button"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <span className="overline gold-text capitalize">{memo.tag}</span>
          <span className="text-xs text-muted-foreground font-mono">{memo.read_time}</span>
          <span className="text-xs text-muted-foreground font-mono ml-auto">{new Date(memo.published_at).toLocaleDateString()}</span>
        </div>
        <h1 className="font-display font-medium text-4xl md:text-5xl tracking-tight leading-tight">{memo.title}</h1>
        <p className="text-lg text-muted-foreground mt-5 leading-relaxed">{memo.subtitle}</p>
        <div className="divider my-8" />

        {memo.locked ? (
          <div className="card-institutional p-10 text-center">
            <Lock className="h-8 w-8 gold-text mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display font-medium text-xl">Upgrade to read this memo</h3>
            <p className="text-sm text-muted-foreground mt-2">This memo is reserved for <span className="gold-text capitalize">{memo.tier_required}</span> members.</p>
            <button onClick={() => navigate("/pricing")} className="btn-primary mt-6" data-testid="memo-upgrade-button">
              Upgrade Access <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <article className="prose prose-invert max-w-none text-[15px] leading-[1.8] whitespace-pre-wrap text-foreground/90">
            {memo.body.split("\n").map((line, i) => {
              const trimmed = line.trim();
              if (trimmed.startsWith("## ")) {
                return <h2 key={i} className="font-display font-medium text-xl mt-8 mb-3">{trimmed.slice(3)}</h2>;
              }
              if (trimmed.startsWith("- ")) {
                return <div key={i} className="flex gap-2 my-1"><span className="gold-text">·</span><span>{trimmed.slice(2)}</span></div>;
              }
              if (trimmed === "") return <div key={i} className="h-3" />;
              return <p key={i} className="my-2">{trimmed}</p>;
            })}
          </article>
        )}

        <div className="divider my-10" />
        <p className="text-xs text-muted-foreground">— {memo.author}</p>
      </div>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        type="memo"
        entityId={memo.id}
        entityTitle={memo.title}
        shareText={shareText}
      />
    </DashboardLayout>
  );
}
